import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useNotifyHook } from '../hooks/useNotify';
import { useAuth } from '../hooks/useAuth';
import {
    FaBus,
    FaUser,
    FaRupeeSign,
    FaCalendarAlt,
    FaClock,
    FaMapMarkerAlt,
    FaChair,
    FaPhone,
    FaVenusMars,
    FaCheck,
    FaLock,
    FaArrowRight,
    FaTag,
    FaShieldAlt,
    FaEnvelope,
    FaUserTie,
    FaCreditCard,
    FaSpinner,
    FaExclamationTriangle,
    FaTimes,
    FaCcVisa,
    FaCcMastercard,
    FaCcAmex,
    FaGooglePay,
    FaPhoneAlt,
    FaWallet,
    FaArrowLeft,
    FaInfoCircle,
    FaReceipt,
    FaEdit,
    FaPercent,
    FaTicketAlt,
    FaChevronDown,
    FaChevronUp,
    FaPlus,
    FaMinus
} from 'react-icons/fa';
import { api } from '../../api';
import { config } from '../../config';

const Book = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const notify = useNotifyHook();
    const { user, isAuthenticated } = useAuth();

    const [bookingData, setBookingData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [passengerDetails, setPassengerDetails] = useState([]);
    const [activePassenger, setActivePassenger] = useState(0);
    const [bookingSession, setBookingSession] = useState(null);
    const [contactInfo, setContactInfo] = useState({
        name: '',
        email: '',
        phone: ''
    });
    const [processingPayment, setProcessingPayment] = useState(false);
    const [razorpayLoaded, setRazorpayLoaded] = useState(false);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('razorpay');
    const [orderTimer, setOrderTimer] = useState(config.orderDuration);
    const [timerActive, setTimerActive] = useState(false);
    const [currentOrderId, setCurrentOrderId] = useState(null);
    const [orderExpired, setOrderExpired] = useState(false);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [detailsConfirmed, setDetailsConfirmed] = useState(false);
    const [paymentSession, setPaymentSession] = useState(null);
    const [contactEditMode, setContactEditMode] = useState(false);
    const [autoFillApplied, setAutoFillApplied] = useState(false);
    
    // Coupon states
    const [coupons, setCoupons] = useState([]);
    const [showMoreCoupons, setShowMoreCoupons] = useState(false);
    const [appliedCoupon, setAppliedCoupon] = useState(null);
    const [customCouponCode, setCustomCouponCode] = useState('');
    const [applyingCoupon, setApplyingCoupon] = useState(false);
    const [discountDetails, setDiscountDetails] = useState(null);
    const [showCouponSection, setShowCouponSection] = useState(false);

    // Auto-fill user data when authenticated
    useEffect(() => {
        if (isAuthenticated && user && !autoFillApplied && !contactEditMode) {
            setContactInfo({
                name: user.name || '',
                email: user.email || '',
                phone: user.phone || ''
            });

            if (passengerDetails.length > 0) {
                const updatedPassengerDetails = [...passengerDetails];
                updatedPassengerDetails[0] = {
                    ...updatedPassengerDetails[0],
                    passengerName: user.name || '',
                    age: user.age || '',
                    gender: user.gender || 'male',
                    phone: user.phone || ''
                };
                setPassengerDetails(updatedPassengerDetails);
            }

            setAutoFillApplied(true);
            notify.info('Your details have been auto-filled for contact and first passenger.');
        }
    }, [isAuthenticated, user, passengerDetails, autoFillApplied, contactEditMode, notify]);

    // Load coupons from booking data
    useEffect(() => {
        if (bookingData?.coupons) {
            setCoupons(bookingData.coupons);
        }
    }, [bookingData]);

    const loadRazorpayScript = () => {
        return new Promise((resolve) => {
            if (window.Razorpay) {
                setRazorpayLoaded(true);
                resolve(true);
                return;
            }

            const script = document.createElement('script');
            script.src = 'https://checkout.razorpay.com/v1/checkout.js';
            script.async = true;
            
            script.onload = () => {
                setRazorpayLoaded(true);
                console.log('Razorpay script loaded successfully');
                resolve(true);
            };
            
            script.onerror = () => {
                console.error('Failed to load Razorpay script');
                notify.error('Payment gateway failed to load. Please refresh.');
                resolve(false);
            };
            
            document.body.appendChild(script);
        });
    };

    useEffect(() => {
        const initialize = async () => {
            await loadRazorpayScript();
            
            try {
                let bookingDataToUse = null;

                if (location.state?.bookingData) {
                    bookingDataToUse = location.state.bookingData;
                    localStorage.setItem('currentBooking', JSON.stringify(bookingDataToUse));
                } else {
                    const savedBooking = localStorage.getItem('currentBooking');
                    if (savedBooking) {
                        bookingDataToUse = JSON.parse(savedBooking);
                    }
                }

                if (bookingDataToUse) {
                    setBookingData(bookingDataToUse);

                    if (bookingDataToUse.seats && bookingDataToUse.seats.length > 0) {
                        const initialDetails = bookingDataToUse.seats.map((seat, index) => ({
                            id: seat.id || `seat-${index}`,
                            seatNumber: seat.seat_number || `S${index + 1}`,
                            passengerName: '',
                            age: '',
                            gender: 'male',
                            phone: ''
                        }));
                        setPassengerDetails(initialDetails);
                    }
                } else {
                    setTimeout(() => {
                        notify.error('No booking data found. Please select seats first.');
                        navigate('/buses');
                    }, 100);
                }
            } catch (error) {
                console.error('Error loading booking data:', error);
                setTimeout(() => {
                    notify.error('Failed to load booking details');
                    navigate('/buses');
                }, 100);
            } finally {
                setLoading(false);
            }
        };

        initialize();

        return () => {
            const razorpayModal = document.querySelector('.razorpay-container');
            if (razorpayModal) {
                razorpayModal.remove();
            }
        };
    }, []);

    useEffect(() => {
        let interval = null;
        
        if (timerActive && orderTimer > 0) {
            interval = setInterval(() => {
                setOrderTimer((prevTimer) => {
                    if (prevTimer <= 1) {
                        clearInterval(interval);
                        setTimerActive(false);
                        setOrderExpired(true);
                        notify.error('Payment session expired. Please create a new order.');
                        return 0;
                    }
                    return prevTimer - 1;
                });
            }, 1000);
        } else if (!timerActive && orderTimer !== 360) {
            clearInterval(interval);
        }
        
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [timerActive, orderTimer]);

    const formatTimer = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const handlePassengerChange = (index, field, value) => {
        setPassengerDetails(prevDetails => {
            const newDetails = [...prevDetails];
            newDetails[index] = {
                ...newDetails[index],
                [field]: value
            };
            return newDetails;
        });
    };

    const handleContactChange = (field, value) => {
        if (isAuthenticated && !contactEditMode && user) {
            notify.info('Click "Edit" to modify your contact details');
            return;
        }
        
        setContactInfo(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const calculateFareBreakdown = () => {
        if (!bookingData) {
            return {
                baseFare: 0,
                convenienceFee: 0,
                gst: 0,
                total: 0
            };
        }

        const baseFare = parseFloat(bookingData.totalFare) || 0;
        const convenienceFee = config.convenienceFee;
        const gst = 0;
        
        // Apply discount if coupon is applied
        let discountedAmount = baseFare;
        if (discountDetails) {
            discountedAmount = discountDetails.final_price;
        }
        
        const total = parseFloat((discountedAmount + convenienceFee + gst).toFixed(2));

        return {
            baseFare,
            convenienceFee,
            gst,
            total,
            discountedAmount,
            discountApplied: discountDetails !== null
        };
    };

    // Apply coupon function
    const applyCoupon = async (couponCode) => {
        if (!bookingData || !bookingData.bus) {
            notify.error('Booking data not found');
            return;
        }

        if (!couponCode.trim()) {
            notify.warning('Please enter a coupon code');
            return;
        }

        setApplyingCoupon(true);

        try {
            const payload = {
                coupon: couponCode.trim(),
                amount: parseFloat(bookingData.totalFare),
                vendor_id: bookingData.bus.vendor_id
            };

            const response = await fetch(api.validateCoupon, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
                },
                body: JSON.stringify(payload)
            });

            const result = await response.json();

            if (result.status && result.success) {
                setDiscountDetails(result.data);
                setAppliedCoupon(couponCode.trim());
                notify.success(result.message || 'Coupon applied successfully!');
                
                // Close coupon section after successful application
                setShowCouponSection(false);
            } else {
                setDiscountDetails(null);
                setAppliedCoupon(null);
                notify.error(result.message || 'Invalid coupon code');
            }
        } catch (error) {
            console.error('Error applying coupon:', error);
            notify.error('Failed to apply coupon. Please try again.');
            setDiscountDetails(null);
            setAppliedCoupon(null);
        } finally {
            setApplyingCoupon(false);
            setCustomCouponCode('');
        }
    };

    // Remove applied coupon
    const removeCoupon = () => {
        setDiscountDetails(null);
        setAppliedCoupon(null);
        notify.info('Coupon removed');
    };

    // Toggle show more coupons
    const toggleShowMoreCoupons = () => {
        setShowMoreCoupons(!showMoreCoupons);
    };

    // Validate form
    const validateForm = () => {
        if (!contactInfo.name.trim() || !contactInfo.phone.trim()) {
            notify.warning('Please fill all contact information');
            return false;
        }

        const phoneRegex = /^[0-9]{10}$/;
        const contactPhone = contactInfo.phone.replace(/\D/g, '');
        if (!phoneRegex.test(contactPhone)) {
            notify.warning('Please enter a valid 10-digit phone number');
            return false;
        }

        if (contactInfo.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactInfo.email)) {
            notify.warning('Please enter a valid email address');
            return false;
        }

        for (let i = 0; i < passengerDetails.length; i++) {
            const p = passengerDetails[i];
            if (!p.passengerName.trim() || !p.age || !p.gender) {
                notify.warning(`Please fill all details for Passenger ${i + 1} (Seat ${p.seatNumber})`);
                setActivePassenger(i);
                return false;
            }

            const age = parseInt(p.age);
            if (age < 1 || age > 100) {
                notify.warning(`Please enter valid age (1-100) for Passenger ${i + 1}`);
                setActivePassenger(i);
                return false;
            }
        }

        if (!termsAccepted) {
            notify.warning('Please accept the terms and conditions');
            return false;
        }

        if (!detailsConfirmed) {
            notify.warning('Please confirm all passenger details are correct');
            return false;
        }

        return true;
    };

    // Create booking session
    const createBooking = async () => {
        try {
            const { total } = calculateFareBreakdown();
            
            const sessionPayload = {
                bus_id: bookingData.bus?.id || 0,
                boarding_id: bookingData.pickupPoint?.id || 0,
                dropping_id: bookingData.dropPoint?.id || 0,
                journey_date: new Date().toISOString().split('T')[0],
                contact: {
                    name: contactInfo.name,
                    mobile: contactInfo.phone,
                    email: contactInfo.email || ''
                },
                passengers: passengerDetails.map((passenger, index) => ({
                    seat_id: bookingData.seats[index]?.id || 0,
                    seat_no: passenger.seatNumber,
                    name: passenger.passengerName,
                    age: parseInt(passenger.age),
                    gender: passenger.gender === 'male' ? 'Male' : 'Female',
                    fare: parseFloat(bookingData.seats[index]?.fare || 0),
                })),
                booking_source: 'web',
                coupon: appliedCoupon || null,
                discount_amount: discountDetails?.discount_amount || 0,
                final_amount: total
            };

            const response = await fetch(api.book, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
                },
                body: JSON.stringify(sessionPayload)
            });
            
            const result = await response.json();
            
            if (result.status) {
                return result.data;
            } else {  
                setProcessingPayment(false);
                throw new Error(result.message);
            }
        } catch (error) {
            setProcessingPayment(false);
            notify.error(error.message);
        }
    };

    // Handle Razorpay payment
    const handleRazorpayPayment = async () => {
        if (!window.Razorpay) {
            notify.error('Payment gateway not loaded. Please refresh.');
            return;
        }

        try {
            setProcessingPayment(true);
            const bookingSession = await createBooking();
            if (!bookingSession?.order_id) {
                return;
            }

            setCurrentOrderId(bookingSession.order_id);
            setOrderTimer(bookingSession.order_duration);
            setTimerActive(true);
            setOrderExpired(false);

            const options = {
                key: bookingSession.key_id || config.razorpay.keyId,
                amount: bookingSession.amount || (calculateFareBreakdown().total * 100),
                currency: 'INR',
                name: 'Bus Booking System',
                description: `Booking for ${bookingData.bus?.name} - ${bookingSession.seats?.join(', ') || bookingData.seats.length} seats`,
                order_id: bookingSession.order_id,
                handler: async (response) => {
                    await verifyPayment({
                        razorpay_order_id: response.razorpay_order_id,
                        razorpay_payment_id: response.razorpay_payment_id,
                        razorpay_signature: response.razorpay_signature,
                        booking_id: bookingSession.booking_id
                    });
                },
                prefill: {
                    name: contactInfo.name,
                    email: contactInfo.email || '',
                    contact: contactInfo.phone
                },
                notes: {
                    booking_id: bookingSession.booking_id,
                    booking_number: bookingSession.booking_number,
                    bus: bookingData.bus?.name,
                    seats: bookingSession.seats?.join(', ')
                },
                theme: {
                    color: '#4F46E5'
                },
                modal: {
                    ondismiss: () => {
                        notify.info('Payment cancelled');
                        setProcessingPayment(false);
                    },
                    escape: false,
                    handleback: false
                },
                retry: {
                    enabled: true,
                    max_count: 2
                },
                timeout: 300,
                // readonly: {
                //     contact: true,
                //     email: contactInfo.email ? true : false
                // }
            };

            const razorpay = new window.Razorpay(options);
            
            razorpay.on('payment.failed', function(response) {
                console.error('Payment failed:', response.error);
                notify.error(`Payment failed: ${response.error.description || 'Unknown error'}`);
                setProcessingPayment(false);
                setOrderExpired(true);
            });

            razorpay.on('payment.authorized', function(response) {
                console.log('Payment authorized:', response);
            });

            razorpay.open();
            
        } catch (error) {
            notify.error(error.message || 'Failed to initialize payment');
            setProcessingPayment(false);
        }
    };

    // Verify payment
    const verifyPayment = async (paymentData) => {
        try {
            const response = await fetch(api.paymentVerify+'/razorpay', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
                },
                body: JSON.stringify(paymentData)
            });

            const result = await response.json();

            if (result.success) {
                localStorage.removeItem('currentBooking');
                notify.success('Payment successful! Booking confirmed.');
                setProcessingPayment(false);
                setTimerActive(false);
                setCurrentOrderId(null);
                
                setTimeout(() => {
                    navigate('/booking-confirmation', {
                        state: {
                            bookingNumber: result.data.booking_number,
                            paymentId: result.data.payment_id,
                            amount: result.data.amount,
                            pickupPoint: result.data.pickupPoint,
                            dropPoint: result.data.dropPoint,
                            ticketLink: result.data.ticketLink,
                        }
                    });
                }, 1500);
            } else {
                throw new Error(result.message || 'Payment verification failed');
            }
        } catch (error) {
            setProcessingPayment(false);
            setTimerActive(false);
            setOrderExpired(true);
        }
    };

    // Retry payment
    const retryPayment = async () => {
        setOrderExpired(false);
        setOrderTimer(bookingSession?.order_duration || 360);
        setCurrentOrderId(null);
        await handleRazorpayPayment();
    };  
    
    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        switch (selectedPaymentMethod) {
            case 'razorpay':
                await handleRazorpayPayment();
                break;
            default:
                notify.error('Please select a payment method');
        }
    };

    // Payment methods
    const paymentMethods = [
        {
            id: 'razorpay',
            name: 'Online Payment',
            icon: FaCreditCard,
            description: 'Pay with Credit/Debit Card, UPI, Net Banking',
            color: 'from-blue-500 to-indigo-600'
        },
    ];

    // Display coupons (limit to 3 initially)
    const displayedCoupons = showMoreCoupons ? coupons : coupons.slice(0, 3);
    const hasMoreCoupons = coupons.length > 3;

    // Loading state
    if (loading) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50">
                <div className="relative">
                    <div className="w-20 h-20 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
                    <FaBus className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-blue-600 text-2xl"/>
                </div>
                <p className="mt-4 text-gray-600 font-medium">Loading booking details...</p>
                {!razorpayLoaded && (
                    <p className="mt-2 text-sm text-gray-500">Loading payment gateway...</p>
                )}
            </div>
        );
    }

    // No booking data state
    if (!bookingData) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50 p-4">
                <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
                    <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <FaBus className="text-red-500 text-3xl"/>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-3">No Booking Data Found</h2>
                    <p className="text-gray-600 mb-6">Please select seats first to proceed with booking.</p>
                    <button 
                        onClick={() => navigate('/buses')}
                        className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-300 flex items-center justify-center gap-2 w-full"
                    >
                        <FaBus/>
                        Go Back to Buses
                    </button>
                </div>
            </div>
        );
    }

    const currentPassenger = passengerDetails[activePassenger] || {};
    const { baseFare, convenienceFee, gst, total, discountedAmount, discountApplied } = calculateFareBreakdown();
    const isFormComplete = passengerDetails.every(p => 
        p.passengerName.trim() && p.age && p.gender
    ) && contactInfo.name.trim() && contactInfo.phone.trim();

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-4 md:p-6">
            <div className="max-w-7xl mx-auto">
              

                {/* User Info Banner for Authenticated Users */}
                {isAuthenticated && user && (
                    <div className="bg-gradient-to-r from-green-50 to-emerald-100 rounded-2xl shadow-lg p-4 mb-6 border border-green-200">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full flex items-center justify-center">
                                    <FaUser className="text-white text-xl" />
                                </div>
                                <div>
                                    <h3 className="font-bold text-gray-800">Welcome, {user.name}!</h3>
                                    <p className="text-sm text-gray-600">
                                        {passengerDetails.length === 1 
                                            ? 'Your details are auto-filled. You can edit passenger details if needed.' 
                                            : 'Your contact and first passenger details are auto-filled. Edit contact info if needed.'}
                                    </p>
                                </div>
                            </div>
                           
                        </div>
                        {!contactEditMode && (
                            <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                                <div className="bg-white p-2 rounded-lg">
                                    <span className="text-gray-500">Phone:</span> {user.phone}
                                </div>
                                <div className="bg-white p-2 rounded-lg">
                                    <span className="text-gray-500">Email:</span> {user.email || 'Not provided'}
                                </div>
                                {user.age && (
                                    <div className="bg-white p-2 rounded-lg">
                                        <span className="text-gray-500">Age:</span> {user.age}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Progress Steps */}
                <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
                    <div className="flex items-center justify-between md:justify-center md:space-x-20">
                        {['Seat Selection', 'Pickup/Drop', 'Passenger Details', 'Payment'].map((step, index) => (
                            <div key={index} className="flex flex-col items-center relative">
                                <div className={`w-12 h-12 rounded-full flex items-center justify-center text-sm font-bold
                                    ${index < 3 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                                    {index < 3 ? <FaCheck/> : 4}
                                </div>
                                <span className={`mt-3 text-sm font-semibold whitespace-nowrap
                                    ${index <= 2 ? 'text-blue-600' : 'text-gray-400'}`}>
                                    {step}
                                </span>
                                {index < 3 && (
                                    <div className="hidden md:block absolute top-6 left-full w-16 h-0.5 bg-blue-600"></div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Header */}
                <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-2xl shadow-2xl p-6 md:p-8 mb-8 text-white relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16"></div>
                    <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-12 -translate-x-12"></div>
                    <div className="relative z-10">
                        <h1 className="text-2xl md:text-4xl font-bold mb-3 flex items-center gap-3">
                            <FaUser className="text-3xl"/>
                            Complete Your Booking
                        </h1>
                        <p className="text-blue-100 text-lg">Review your selection and fill passenger details</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Left Column - Journey Details & Passenger Form */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Journey Summary Card */}
                        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
                            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 border-b border-gray-200">
                                <div className="flex justify-between items-center">
                                    <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                                        <FaBus className="text-blue-600"/>
                                        Journey Details
                                    </h2>
                                    <span className="bg-blue-100 text-blue-700 text-sm font-semibold px-4 py-1.5 rounded-full">
                                        Ref: #{bookingData.bus?.id || 'N/A'}
                                    </span>
                                </div>
                            </div>

                            <div className="p-6">
                                {/* Route Timeline */}
                                <div className="relative mb-8">
                                    <div className="flex items-start mb-10">
                                        <div className="bg-green-500 w-5 h-5 rounded-full mt-1 flex-shrink-0 ring-4 ring-green-100"></div>
                                        <div className="ml-5 flex-1">
                                            <div className="flex items-center gap-2 mb-1">
                                                <FaClock className="text-green-500"/>
                                                <span className="font-semibold text-gray-800">
                                                    {bookingData.pickupPoint?.time || '--:--'}
                                                </span>
                                            </div>
                                            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                                <FaMapMarkerAlt className="text-green-500"/> 
                                                {bookingData.pickupPoint?.name || 'Pickup Point'}
                                            </h3>
                                            <p className="text-gray-600 text-sm mt-1">
                                                {bookingData.pickupPoint?.address || ''}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="absolute left-[10px] top-6 bottom-6 w-0.5 bg-gradient-to-b from-green-500 to-blue-500"></div>

                                    <div className="flex items-start">
                                        <div className="bg-blue-500 w-5 h-5 rounded-full mt-1 flex-shrink-0 ring-4 ring-blue-100"></div>
                                        <div className="ml-5 flex-1">
                                            <div className="flex items-center gap-2 mb-1">
                                                <FaClock className="text-blue-500"/>
                                                <span className="font-semibold text-gray-800">
                                                    {bookingData.dropPoint?.time || '--:--'}
                                                </span>
                                            </div>
                                            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                                <FaMapMarkerAlt className="text-blue-500"/> 
                                                {bookingData.dropPoint?.name || 'Drop Point'}
                                            </h3>
                                            <p className="text-gray-600 text-sm mt-1">
                                                {bookingData.dropPoint?.address || ''}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Bus Details */}
                                <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-5 border border-gray-200">
                                    <div className="flex items-center justify-between mb-4">
                                        <div>
                                            <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                                                <FaBus className="text-blue-600"/> 
                                                {bookingData.bus?.name || 'Bus Name'}
                                            </h3>
                                            <span className="text-sm text-gray-600">
                                                {bookingData.bus?.type || 'AC Sleeper'}
                                            </span>
                                        </div>
                                        <span className="bg-blue-100 text-blue-700 text-sm font-semibold px-4 py-1.5 rounded-full">
                                            {bookingData.seatCount || 0} Seats
                                        </span>
                                    </div>

                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                                            <div className="flex items-center gap-2 mb-2">
                                                <FaCalendarAlt className="text-blue-500"/>
                                                <span className="text-sm text-gray-600">Travel Date</span>
                                            </div>
                                            <div className="font-bold text-gray-900">
                                                {new Date().toLocaleDateString('en-IN', { 
                                                    weekday: 'short', 
                                                    day: 'numeric',
                                                    month: 'short',
                                                    year: 'numeric'
                                                })}
                                            </div>
                                        </div>
                                        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                                            <div className="flex items-center gap-2 mb-2">
                                                <FaClock className="text-blue-500"/>
                                                <span className="text-sm text-gray-600">Departure</span>
                                            </div>
                                            <div className="font-bold text-gray-900">
                                                {bookingData.pickupPoint?.time || '--:--'}
                                            </div>
                                        </div>
                                        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                                            <div className="flex items-center gap-2 mb-2">
                                                <FaTag className="text-blue-500"/>
                                                <span className="text-sm text-gray-600">Status</span>
                                            </div>
                                            <div className="font-bold text-green-600">Processing</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Passenger Navigation Tabs */}
                        {passengerDetails.length > 1 && (
                            <div className="bg-white rounded-2xl shadow-lg p-5">
                                <div className="flex space-x-2 overflow-x-auto pb-3">
                                    {passengerDetails.map((passenger, index) => (
                                        <button 
                                            key={index}
                                            type="button"
                                            onClick={() => setActivePassenger(index)}
                                            className={`
                                                flex items-center gap-2 px-5 py-3 rounded-xl whitespace-nowrap flex-shrink-0 transition-all
                                                ${activePassenger === index 
                                                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg' 
                                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                }
                                            `}
                                        >
                                            <FaUser className="text-sm"/>
                                            Passenger {index + 1}
                                            {passenger.passengerName && passenger.age  ? (
                                                <FaCheck className="text-xs ml-1"/>
                                            ) : (
                                                <span className="w-2 h-2 bg-red-500 rounded-full ml-2"></span>
                                            )}
                                            {isAuthenticated && user && index === 0 && !contactEditMode && (
                                                <span className="ml-1 text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">You</span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                                <p className="text-sm text-gray-600 mt-4">
                                    Currently editing: <span className="font-bold text-blue-600">Seat {currentPassenger.seatNumber}</span>
                                    {isAuthenticated && user && activePassenger === 0 && !contactEditMode && (
                                        <span className="ml-2 text-green-600 font-medium">(Auto-filled from your profile)</span>
                                    )}
                                </p>
                            </div>
                        )}

                        {/* Passenger Details Form */}
                        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                            <div className="bg-gradient-to-r from-green-50 to-teal-50 p-6 border-b border-gray-200">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                                            <FaUser className="text-green-600"/>
                                            {passengerDetails.length > 1 ? `Passenger ${activePassenger + 1} Details` : 'Passenger Details'}
                                            <span className="ml-2 bg-green-100 text-green-700 text-sm font-semibold px-4 py-1.5 rounded-full">
                                                Seat {currentPassenger.seatNumber}
                                            </span>
                                        </h2>
                                        <p className="text-gray-600 text-sm mt-1">
                                            {isAuthenticated && user && activePassenger === 0 
                                                ? 'Your details are auto-filled. You can edit if needed.' 
                                                : 'Fill details for passenger'}
                                        </p>
                                    </div>
                                    {isAuthenticated && user && activePassenger === 0 && (
                                        <span className="bg-blue-100 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full">
                                            {!contactEditMode ? 'Auto-filled' : 'Editable'}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {passengerDetails.length > 0 && (
                                <div className="p-6 space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                Full Name *
                                                {isAuthenticated && user && activePassenger === 0 && !contactEditMode && (
                                                    <span className="ml-2 text-xs text-green-600">(From your profile)</span>
                                                )}
                                            </label>
                                            <input 
                                                type="text"
                                                value={passengerDetails[activePassenger]?.passengerName || ''}
                                                onChange={(e) => handlePassengerChange(activePassenger, 'passengerName', e.target.value)}
                                                placeholder="Enter full name as per ID"
                                                className="w-full px-4 py-3.5 border border-gray-300 rounded-xl focus:ring-3 focus:ring-blue-500/30 focus:border-blue-500 transition"
                                                required
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                Age *
                                                {isAuthenticated && user && activePassenger === 0 && !contactEditMode && (
                                                    <span className="ml-2 text-xs text-green-600">(From your profile)</span>
                                                )}
                                            </label>
                                            <input 
                                                type="number"
                                                value={passengerDetails[activePassenger]?.age || ''}
                                                onChange={(e) => handlePassengerChange(activePassenger, 'age', e.target.value)}
                                                placeholder="Enter age"
                                                min="1"
                                                max="100"
                                                className="w-full px-4 py-3.5 border border-gray-300 rounded-xl focus:ring-3 focus:ring-blue-500/30 focus:border-blue-500 transition"
                                                required
                                            />
                                        </div>

                                        <div className="md:col-span-2">
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                <FaVenusMars className="inline mr-2"/>
                                                Gender *
                                            </label>
                                            <div className="flex space-x-6">
                                                {[
                                                    { value: 'male', label: 'Male' },
                                                    { value: 'female', label: 'Female' }
                                                ].map((option) => (
                                                    <label key={option.value} className="flex items-center space-x-3 cursor-pointer">
                                                        <input 
                                                            type="radio"
                                                            name={`gender-${activePassenger}`}
                                                            value={option.value}
                                                            checked={passengerDetails[activePassenger]?.gender === option.value}
                                                            onChange={(e) => handlePassengerChange(activePassenger, 'gender', e.target.value)}
                                                            className="w-5 h-5 text-blue-600 focus:ring-blue-500"
                                                        />
                                                        <span className="text-gray-700 font-medium">{option.label}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Current Passenger Status */}
                                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-5">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <h4 className="font-bold text-blue-800">
                                                    Passenger {activePassenger + 1} of {passengerDetails.length}
                                                    {isAuthenticated && user && activePassenger === 0 && (
                                                        <span className="ml-2 text-green-600">(You)</span>
                                                    )}
                                                </h4>
                                                <p className="text-sm text-blue-600 mt-1">
                                                    Seat: {passengerDetails[activePassenger]?.seatNumber}
                                                </p>
                                            </div>
                                            <div className="text-right">
                                                <div className={`text-lg font-bold ${
                                                    passengerDetails[activePassenger]?.passengerName && 
                                                    passengerDetails[activePassenger]?.age 
                                                        ? 'text-green-600' 
                                                        : 'text-yellow-600'
                                                }`}>
                                                    {passengerDetails[activePassenger]?.passengerName && 
                                                     passengerDetails[activePassenger]?.age 
                                                        ? '✓ Complete' 
                                                        : '✗ Incomplete'}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Contact Information Card */}
                        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                            <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 border-b border-gray-200">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                                            <FaUserTie className="text-purple-600"/>
                                            Contact Information
                                        </h2>
                                        <p className="text-gray-600 text-sm mt-1">Primary contact for booking updates</p>
                                    </div>
                                    {isAuthenticated && user && !contactEditMode && (
                                        <span className="bg-green-100 text-green-700 text-xs font-semibold px-3 py-1 rounded-full">
                                            Auto-filled from profile
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="p-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Contact Person Name *
                                        </label>
                                        <input 
                                            type="text"
                                            value={contactInfo.name}
                                            onChange={(e) => handleContactChange('name', e.target.value)}
                                            placeholder="Enter your full name"
                                            className={`w-full px-4 py-3.5 border rounded-xl focus:ring-3 focus:ring-blue-500/30 focus:border-blue-500 transition ${
                                                isAuthenticated && !contactEditMode
                                                    ? 'bg-gray-50 border-gray-300 text-gray-700'
                                                    : 'border-gray-300'
                                            }`}
                                            required
                                            // readOnly={isAuthenticated && !contactEditMode}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            <FaEnvelope className="inline mr-2"/>
                                            Email Address (Optional)
                                        </label>
                                        <input 
                                            type="email"
                                            value={contactInfo.email}
                                            onChange={(e) => handleContactChange('email', e.target.value)}
                                            placeholder="Enter your email"
                                            className={`w-full px-4 py-3.5 border rounded-xl focus:ring-3 focus:ring-blue-500/30 focus:border-blue-500 transition ${
                                                isAuthenticated && !contactEditMode
                                                    ? 'bg-gray-50 border-gray-300 text-gray-700'
                                                    : 'border-gray-300'
                                            }`}
                                            // readOnly={isAuthenticated && !contactEditMode}
                                        />
                                    </div>

                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Mobile Number *
                                        </label>
                                        <input 
                                            type="tel"
                                            value={contactInfo.phone}
                                            onChange={(e) => handleContactChange('phone', e.target.value)}
                                            placeholder="Enter 10-digit mobile number"
                                            className={`w-full px-4 py-3.5 border rounded-xl focus:ring-3 focus:ring-blue-500/30 focus:border-blue-500 transition ${
                                                isAuthenticated && !contactEditMode
                                                    ? 'bg-gray-50 border-gray-300 text-gray-700'
                                                    : 'border-gray-300'
                                            }`}
                                            pattern="[0-9]{10}"
                                            required
                                            // readOnly={isAuthenticated && !contactEditMode}
                                        />
                                    </div>
                                </div>
                                {isAuthenticated && user && !contactEditMode && (
                                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                        <p className="text-sm text-blue-700">
                                            <FaInfoCircle className="inline mr-1" />
                                            Click "Edit Contact" button above to modify contact details
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Right Column - Payment Summary */}
                    <div className="space-y-6">
                        {/* Timer Display */}
                        {timerActive && (
                            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 border-b border-blue-200">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <FaClock className="text-blue-600" />
                                            <span className="text-sm font-semibold text-blue-800">
                                                Payment Session Expires In:
                                            </span>
                                        </div>
                                        <div className={`text-lg font-bold ${orderTimer < 60 ? 'text-red-600 animate-pulse' : 'text-green-600'}`}>
                                            {formatTimer(orderTimer)}
                                        </div>
                                    </div>
                                </div>
                                <div className="p-4">
                                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                                        {bookingSession ? (
                                            <div 
                                                className={`h-1.5 rounded-full ${orderTimer < 60 ? 'bg-red-500' : 'bg-blue-500'}`}
                                                style={{ width: `${(orderTimer / (bookingSession?.order_duration || 360)) * 100}%` }}
                                            ></div>
                                        ) : (
                                            <div className="h-1.5 bg-gray-400 rounded-full"></div> 
                                        )}
                                    </div>
                                    <p className="text-xs text-gray-600 mt-2">
                                        Complete payment before timer expires to secure your seats
                                    </p>
                                </div>
                            </div>
                        )}

                        {orderExpired && (
                            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                                <div className="bg-gradient-to-r from-red-50 to-orange-50 p-4 border-b border-red-200">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <FaExclamationTriangle className="text-red-600" />
                                            <span className="text-sm font-semibold text-red-800">
                                                Payment Session Expired
                                            </span>
                                        </div>
                                        <button
                                            onClick={retryPayment}
                                            className="bg-red-600 hover:bg-red-700 text-white text-sm font-semibold px-4 py-1.5 rounded-lg transition"
                                        >
                                            Repay
                                        </button>
                                    </div>
                                </div>
                                <div className="p-4">
                                    <p className="text-xs text-red-600">
                                        Previous payment session has expired. Please create a new payment order.
                                    </p>
                                </div>
                            </div>
                        )}

            {bookingData.isCoupon == "1" && (
                        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                            <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 border-b border-gray-200">
                                <div className="flex items-center justify-between">
                                    <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                                        <FaTicketAlt className="text-purple-600"/>
                                        Apply Coupon
                                    </h2>
                                    <button
                                        type="button"
                                        onClick={() => setShowCouponSection(!showCouponSection)}
                                        className="text-sm font-medium text-purple-600 hover:text-purple-700 flex items-center gap-1"
                                    >
                                        {showCouponSection ? 'Hide' : 'Show'} Coupons
                                        {showCouponSection ? <FaChevronUp /> : <FaChevronDown />}
                                    </button>
                                </div>
                            </div>

                            <div className="p-6 space-y-4">
                                {/* Applied Coupon Display */}
                                {appliedCoupon && discountDetails && (
                                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-4">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <div className="flex items-center gap-2 mb-1">
                                                    <FaCheck className="text-green-600" />
                                                    <span className="font-bold text-green-700">Coupon Applied!</span>
                                                </div>
                                                <p className="text-sm text-green-600">
                                                    <span className="font-semibold">{appliedCoupon}</span> - {discountDetails.discount_type === 'percentage' ? `₹${discountDetails.discount_amount} OFF` : `₹${discountDetails.discount_amount} OFF`}
                                                </p>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={removeCoupon}
                                                className="text-red-600 hover:text-red-700 text-sm font-medium"
                                            >
                                                Remove
                                            </button>
                                        </div>
                                        <div className="mt-3 pt-3 border-t border-green-200">
                                            <div className="flex justify-between text-sm">
                                                <span>Original Price:</span>
                                                <span className="line-through text-gray-500">₹{discountDetails.original_price}</span>
                                            </div>
                                            <div className="flex justify-between text-sm">
                                                <span>Discount:</span>
                                                <span className="text-green-600 font-bold">-₹{discountDetails.discount_amount}</span>
                                            </div>
                                            <div className="flex justify-between font-bold mt-1">
                                                <span>Final Price:</span>
                                                <span className="text-green-700">₹{discountDetails.final_price}</span>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Coupon Input */}
                                {showCouponSection && (
                                    <>
                                        <div className="flex gap-2">
                                            <input
                                                type="text"
                                                value={customCouponCode}
                                                onChange={(e) => setCustomCouponCode(e.target.value)}
                                                placeholder="Enter coupon code"
                                                className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => applyCoupon(customCouponCode)}
                                                disabled={applyingCoupon}
                                                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold px-5 rounded-xl transition flex items-center gap-2 disabled:opacity-50"
                                            >
                                                {applyingCoupon ? (
                                                    <>
                                                        <FaSpinner className="animate-spin" />
                                                        Applying...
                                                    </>
                                                ) : (
                                                    'Apply'
                                                )}
                                            </button>
                                        </div>

                                        {/* Available Coupons */}
                                        {coupons.length > 0 && (
                                            <div className="space-y-3">
                                                <div className="flex items-center justify-between">
                                                    <p className="text-sm font-medium text-gray-700">Available Coupons:</p>
                                                    {hasMoreCoupons && (
                                                        <button
                                                            type="button"
                                                            onClick={toggleShowMoreCoupons}
                                                            className="text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1"
                                                        >
                                                            {showMoreCoupons ? 'Show Less' : `Show ${coupons.length - 3} More`}
                                                            {showMoreCoupons ? <FaChevronUp /> : <FaChevronDown />}
                                                        </button>
                                                    )}
                                                </div>
                                                
                                                {displayedCoupons.map((coupon, index) => (
                                                    <div 
                                                        key={coupon.id}
                                                        className={`p-3 border rounded-lg cursor-pointer transition-all hover:shadow-md ${
                                                            appliedCoupon === coupon.code 
                                                                ? 'border-green-500 bg-green-50' 
                                                                : 'border-gray-200 hover:border-purple-300'
                                                        }`}
                                                        onClick={() => applyCoupon(coupon.code)}
                                                    >
                                                        <div className="flex items-center justify-between">
                                                            <div className="flex items-center gap-3">
                                                                <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                                                                    <FaPercent className="text-white" />
                                                                </div>
                                                                <div>
                                                                    <h4 className="font-bold text-gray-800">{coupon.code}</h4>
                                                                    <p className="text-sm text-gray-600">
                                                                        {coupon.discount_type === 'percentage' 
                                                                            ? `${coupon.discount}% OFF` 
                                                                            : `₹${coupon.discount} OFF`}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            {appliedCoupon === coupon.code ? (
                                                                <FaCheck className="text-green-600" />
                                                            ) : (
                                                                <button
                                                                    type="button"
                                                                    className="text-purple-600 hover:text-purple-700 text-sm font-medium"
                                                                >
                                                                    Apply
                                                                </button>
                                                            )}
                                                        </div>
                                                        <p className="text-xs text-gray-500 mt-2">
                                                            Valid until: {new Date(coupon.expire_at).toLocaleDateString('en-IN')}
                                                        </p>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
            )}

                        {/* Payment Method Selection */}
                        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                            <div className="bg-gradient-to-r from-orange-50 to-yellow-50 p-6 border-b border-gray-200">
                                <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                                    <FaCreditCard className="text-orange-600"/>
                                    Select Payment Method
                                </h2>
                            </div>

                            <div className="p-6 space-y-4">
                                {paymentMethods.map((method) => (
                                    <div 
                                        key={method.id}
                                        onClick={() => setSelectedPaymentMethod(method.id)}
                                        className={`p-4 border-2 rounded-xl cursor-pointer transition-all duration-300
                                            ${selectedPaymentMethod === method.id 
                                                ? 'border-blue-500 bg-blue-50' 
                                                : 'border-gray-200 hover:border-gray-300'
                                            }`}
                                    >
                                        <div className="flex items-center gap-4">
                                            <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${method.color} flex items-center justify-center`}>
                                                <method.icon className="text-white text-xl" />
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex items-center justify-between">
                                                    <h3 className="font-bold text-gray-800">{method.name}</h3>
                                                    {selectedPaymentMethod === method.id && (
                                                        <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                                                            <FaCheck className="text-white text-xs" />
                                                        </div>
                                                    )}
                                                </div>
                                                <p className="text-sm text-gray-600 mt-1">{method.description}</p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Payment Summary */}
                        <div className="bg-white rounded-2xl shadow-xl overflow-hidden sticky top-6">
                            <div className="bg-gradient-to-r from-green-50 to-teal-50 p-6 border-b border-gray-200">
                                <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                                    <FaRupeeSign className="text-green-600"/>
                                    Payment Summary
                                </h2>
                            </div>

                            <div className="p-6">
                                {/* Fare Breakdown */}
                                <div className="space-y-4 mb-6">
                                    <div className="flex justify-between items-center py-2">
                                        <span className="text-gray-600">
                                            Base Fare ({bookingData.seatCount || 0} seats)
                                        </span>
                                        <span className="font-bold text-gray-800">
                                            ₹{baseFare.toFixed(2)}
                                        </span>
                                    </div>

                                    {/* Discount Display */}
                                    {discountApplied && discountDetails && (
                                        <div className="flex justify-between items-center py-2 bg-green-50 p-3 rounded-lg">
                                            <span className="text-green-700">
                                                Discount Applied ({discountDetails.coupon_code})
                                             
                                            </span>
                                            <span className="font-bold text-green-700">
                                                -₹{discountDetails.discount_amount.toFixed(2)}
                                            </span>
                                        </div>
                                    )}

                                    {discountApplied && (
                                        <div className="flex justify-between items-center py-2">
                                            <span className="text-gray-600">Price After Discount</span>
                                            <span className="font-bold text-gray-800">
                                                ₹{discountedAmount.toFixed(2)}
                                            </span>
                                        </div>
                                    )}

                                    <div className="flex justify-between items-center py-2">
                                        <span className="text-gray-600">Convenience Fee</span>
                                        <span className="font-bold text-gray-800">₹{convenienceFee.toFixed(2)}</span>
                                    </div>

                                    <div className="border-t-2 border-gray-300 pt-4 mt-2">
                                        <div className="flex justify-between items-center">
                                            <span className="text-lg font-bold text-gray-800">Total Amount</span>
                                            <span className="text-2xl font-bold text-green-600">
                                                ₹{total.toFixed(2)}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Selected Seats Preview */}
                                <div className="bg-gray-50 rounded-xl p-4 mb-6">
                                    <h4 className="font-semibold text-gray-700 mb-3">Selected Seats:</h4>
                                    <div className="flex flex-wrap gap-2">
                                        {bookingData.seats?.map((seat, index) => (
                                            <span key={index} className="bg-white px-3 py-1.5 rounded-lg border border-gray-300 text-sm font-medium">
                                                {seat.seat_number}
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                {/* Terms and Conditions */}
                                <div className="bg-gray-50 rounded-xl p-4 mb-6">
                                    <div className="space-y-3">
                                        <label className="flex items-start gap-3 cursor-pointer">
                                            <input 
                                                type="checkbox" 
                                                checked={termsAccepted}
                                                onChange={(e) => setTermsAccepted(e.target.checked)}
                                                className="w-5 h-5 mt-1 text-blue-600 rounded focus:ring-blue-500"
                                            />
                                            <span className="text-sm text-gray-700">
                                                I agree to the{' '}
                                                <a href="/terms-conditions" target="_blank" className="text-blue-600 hover:underline font-medium">Terms & Conditions</a>
                                            </span>
                                        </label>
                                        <label className="flex items-start gap-3 cursor-pointer">
                                            <input 
                                                type="checkbox" 
                                                checked={detailsConfirmed}
                                                onChange={(e) => setDetailsConfirmed(e.target.checked)}
                                                className="w-5 h-5 mt-1 text-blue-600 rounded focus:ring-blue-500"
                                            />
                                            <span className="text-sm text-gray-700">
                                                I confirm all passenger details are correct
                                            </span>
                                        </label>
                                    </div>
                                </div>

                                {/* Pay Now Button */}
                                <form onSubmit={handleSubmit}>
                                    {processingPayment ? (
                                        <button
                                            type="button"
                                            disabled
                                            className="w-full bg-gradient-to-r from-blue-400 to-indigo-400 text-white font-bold py-4 px-6 rounded-xl shadow-lg flex items-center justify-center gap-3 cursor-not-allowed"
                                        >
                                            <FaSpinner className="animate-spin text-xl" />
                                            <span className="text-lg">Processing Payment...</span>
                                        </button>
                                    ) : (
                                        <button
                                            type="submit"
                                            disabled={!isFormComplete || !razorpayLoaded}
                                            className={`w-full font-bold py-4 px-6 rounded-xl shadow-lg transition-all duration-300 flex items-center justify-center gap-3 group
                                                ${isFormComplete && razorpayLoaded 
                                                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white hover:shadow-2xl transform hover:-translate-y-1' 
                                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                }`}
                                        >
                                            <FaRupeeSign className="text-xl group-hover:scale-110 transition-transform" />
                                            <span className="text-lg">Pay ₹{total.toFixed(2)} Now</span>
                                            <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
                                        </button>
                                    )}
                                </form>

                                {/* Security Badge */}
                                <div className="mt-6 pt-6 border-t border-gray-200">
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-2 text-sm text-gray-600">
                                            <FaLock className="text-green-500" />
                                            <span>Secure payment</span>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <FaCcVisa className="text-blue-600 text-xl" />
                                            <FaCcMastercard className="text-red-600 text-xl" />
                                            <FaCcAmex className="text-blue-800 text-xl" />
                                            <FaGooglePay className="text-gray-800 text-xl" />
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 text-xs text-gray-500">
                                        <FaShieldAlt className="text-blue-500" />
                                        <span>256-bit SSL encryption • PCI DSS compliant</span>
                                    </div>
                                </div>

                                {/* Razorpay Status */}
                                {!razorpayLoaded && (
                                    <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                        <div className="flex items-center gap-2 text-yellow-700 text-sm">
                                            <FaExclamationTriangle />
                                            <span>Payment gateway loading...</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Book;